import { company } from '@awpmarine/core/company';
import { Link, type LinkProps, createFileRoute } from '@tanstack/react-router';

import { ClientLogos, ContactCta, Eyebrow, Photo } from '@/components/marketing';
import { NewsCard, newsItems } from '@/components/news';
import BridgeScreensImg from '@/images/marketing/bridge-screens.jpg';
import BulkAnchoredImg from '@/images/marketing/bulk-anchored-hills.jpg';
import BulkLoadingImg from '@/images/marketing/bulk-loading.jpg';
import BulkWithTugsImg from '@/images/marketing/bulk-with-tugs.jpg';
import TankerAlongsideImg from '@/images/marketing/tanker-alongside.jpg';
import { pageHead } from '@/lib/head';
import { languageTag } from '@/lib/locale';
import { ogImage } from '@/lib/open-graph';
import { serviceCategories } from '@/lib/services';
import { m } from '@/paraglide/messages';
import { config, experienceYears, stats } from '@/site-config';

export const metadata = () => ({ title: m.home_title(), description: m.home_description(), absoluteTitle: true });

export const Route = createFileRoute('/')({
  head: () =>
    pageHead({
      ...metadata(),
      canonical: '/',
      image: ogImage('/'),
      // The name Google shows for the site in results.
      scripts: [
        {
          type: 'application/ld+json',
          children: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            'name': company.shortName,
            'alternateName': company.legalName,
            'url': `${config.siteUrl}/`,
          }),
        },
      ],
    }),
  component: HomePage,
});

const serviceImages: Record<string, { src: string; position?: string }> = {
  'vessel-inspections': { src: TankerAlongsideImg },
  'surveys-consultancy': { src: BulkLoadingImg, position: '50% 60%' },
  'audits-assessments': { src: BridgeScreensImg, position: '50% 45%' },
};

function HomePage() {
  const latest = newsItems().slice(0, 3);
  const reasons: ({ title: string; text: string } & Pick<LinkProps, 'to' | 'hash'>)[] = [
    { title: m.home_reason_sea_title(), text: m.home_reason_sea_text() },
    { title: m.home_reason_accredited_title(), text: m.home_reason_accredited_text() },
    { title: m.home_reason_reviewed_title(), text: m.home_reason_reviewed_text() },
    {
      title: m.home_reason_anywhere_title(),
      text: m.home_reason_anywhere_text({
        ports: stats.ports.toLocaleString(languageTag()),
        countries: stats.countries.toLocaleString(languageTag()),
      }),
      to: '/services',
      hash: 'where-we-work',
    },
  ];

  return (
    <>
      <section className='mx-auto flex w-full max-w-310 flex-wrap items-center gap-14 px-5 pt-12 pb-18 sm:px-8 sm:pt-14 sm:pb-22'>
        <div className='min-w-0 flex-[1_1_460px]'>
          <Eyebrow className='mb-5'>{m.home_eyebrow()}</Eyebrow>
          <h1 className='mb-6 text-[clamp(40px,5vw,62px)] leading-[1.08] font-bold tracking-[-0.02em]'>
            {m.home_hero_title()}
          </h1>
          <p className='text-body mb-9 max-w-130 text-[19px] leading-[1.65]'>
            {m.home_hero_lead({ ports: stats.ports.toLocaleString(languageTag()) })}
          </p>
          <div className='flex flex-wrap gap-3'>
            <Link
              to='/contact'
              className='inline-flex min-h-12 items-center justify-center gap-2.5 rounded-full bg-brand px-7 font-semibold text-white transition-colors hover:bg-[#1f4f8a] disabled:opacity-70'
            >
              {m.home_contact()}
            </Link>
            <Link
              to='/services'
              className='inline-flex min-h-12 items-center justify-center gap-2.5 rounded-full border border-[#cfd7e2] bg-white px-7 font-semibold transition-colors hover:border-brand hover:text-brand'
            >
              {m.home_services()}
            </Link>
          </div>
        </div>
        <div className='relative min-w-0 flex-[1_1_520px]'>
          <Photo src={BulkAnchoredImg} alt={m.home_hero_image()} position='50% 55%' priority className='aspect-4/3' />
          <div className='absolute inset-x-5 bottom-5 flex flex-wrap gap-2'>
            <span className='rounded-full bg-white/95 px-4 py-2 text-sm font-semibold'>{m.home_badge_ocimf()}</span>
            <span className='rounded-full bg-white/95 px-4 py-2 text-sm font-semibold'>{m.home_badge_iso()}</span>
          </div>
        </div>
      </section>

      <section className='bg-mist'>
        <div className='mx-auto grid w-full max-w-310 grid-cols-2 items-center gap-6 px-5 py-10 sm:grid-cols-[repeat(auto-fit,minmax(200px,1fr))] sm:px-8'>
          {[
            { value: `${experienceYears}`, label: m.home_stat_years() },
            { value: `${stats.inspections.toLocaleString(languageTag())}+`, label: m.home_stat_inspections() },
            { value: `${stats.ports.toLocaleString(languageTag())}+`, label: m.home_stat_ports() },
            { value: `${stats.countries.toLocaleString(languageTag())}+`, label: m.home_stat_countries() },
          ].map((stat) => (
            <div key={stat.label}>
              <p className='text-[34px] font-bold tracking-[-0.02em] sm:text-[40px]'>{stat.value}</p>
              <p className='text-subtle'>{stat.label}</p>
            </div>
          ))}
        </div>
      </section>

      <section className='mx-auto w-full max-w-310 px-5 py-14 sm:px-8 sm:py-24'>
        <div className='mb-11 flex flex-wrap items-end justify-between gap-6'>
          <div className='max-w-155'>
            <Eyebrow>{m.home_what_we_do()}</Eyebrow>
            <h2 className='text-[clamp(30px,3vw,42px)] leading-[1.12] font-bold tracking-[-0.015em]'>
              {m.home_what_we_do_title()}
            </h2>
          </div>
          <Link to='/services' className='border-b border-current pb-0.5 font-semibold'>
            {m.home_all_services()}
          </Link>
        </div>
        <div className='grid grid-cols-[repeat(auto-fit,minmax(300px,1fr))] gap-6'>
          {serviceCategories.map((category) => (
            <Link
              key={category.slug}
              to='/services'
              hash={category.slug}
              className='bg-mist flex flex-col overflow-hidden rounded-[20px] transition-transform hover:-translate-y-0.5'
            >
              <Photo
                src={serviceImages[category.slug]!.src}
                position={serviceImages[category.slug]!.position}
                alt=''
                className='h-50 rounded-none'
              />
              <span className='flex flex-col gap-3 p-7'>
                <span className='text-[22px] font-bold'>{category.title()}</span>
                <span className='text-body leading-relaxed'>{category.tagline()}</span>
              </span>
            </Link>
          ))}
        </div>
      </section>

      <ClientLogos label={m.home_trusted_by()} />

      <section className='mx-auto flex w-full max-w-310 flex-wrap gap-16 px-5 py-14 sm:px-8 sm:py-24'>
        <div className='min-w-0 flex-[1_1_440px] self-center'>
          <Eyebrow>{m.home_why()}</Eyebrow>
          <h2 className='mb-9 text-[clamp(30px,3vw,42px)] leading-[1.12] font-bold tracking-[-0.015em]'>
            {m.home_why_title()}
          </h2>
          <div className='flex flex-col gap-7'>
            {reasons.map((reason) => (
              <div key={reason.title} className='border-l-3 border-brand pl-5'>
                <h3 className='mb-1.5 text-[19px] font-bold'>{reason.title}</h3>
                <p className='text-body leading-relaxed'>
                  {reason.to ? (
                    <Link to={reason.to} hash={reason.hash} className='underline underline-offset-4 hover:text-brand'>
                      {reason.text}
                    </Link>
                  ) : (
                    reason.text
                  )}
                </p>
              </div>
            ))}
          </div>
        </div>
        <Photo
          src={BulkWithTugsImg}
          alt={m.home_why_image()}
          position='55% 50%'
          className='min-h-90 min-w-0 flex-[1_1_440px]'
        />
      </section>

      {latest.length > 0 && (
        <section className='bg-mist'>
          <div className='mx-auto w-full max-w-310 px-5 py-14 sm:px-8 sm:py-24'>
            <div className='mb-10 flex flex-wrap items-end justify-between gap-6'>
              <h2 className='text-[clamp(30px,3vw,42px)] leading-[1.12] font-bold tracking-[-0.015em]'>
                {m.home_latest_news()}
              </h2>
              <Link to='/news' className='border-b border-current pb-0.5 font-semibold'>
                {m.home_all_news()}
              </Link>
            </div>
            <div className='grid grid-cols-[repeat(auto-fit,minmax(300px,1fr))] gap-5'>
              {latest.map((item) => (
                <NewsCard key={item.slug} item={item} />
              ))}
            </div>
          </div>
        </section>
      )}

      <ContactCta />
    </>
  );
}
